import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import data from '@emoji-mart/data';

type EmojiMartCategory = {
  id: string;
  emojis: string[];
};

type EmojiMartSkin = { native?: string };
type EmojiMartEntry = { skins?: EmojiMartSkin[] };

type EmojiMartData = {
  categories: EmojiMartCategory[];
  emojis: Record<string, EmojiMartEntry>;
};

const emojiData = data as unknown as EmojiMartData;

const CATEGORY_META: {
  id: string;
  label: string;
  icon: string;
}[] = [
  { id: 'people', label: '스마일리 및 사람', icon: '😀' },
  { id: 'nature', label: '동물 및 자연', icon: '🐻' },
  { id: 'foods', label: '음식 및 음료', icon: '🍔' },
  { id: 'activity', label: '활동', icon: '⚽' },
  { id: 'places', label: '여행 및 장소', icon: '🚗' },
  { id: 'objects', label: '사물', icon: '💡' },
  { id: 'symbols', label: '기호', icon: '🔣' },
  { id: 'flags', label: '깃발', icon: '🏳️' },
];

function buildCategoryEmojis(): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const cat of emojiData.categories ?? []) {
    const list: string[] = [];
    const seen = new Set<string>();
    for (const emojiId of cat.emojis ?? []) {
      const entry = emojiData.emojis[emojiId];
      const native = entry?.skins?.[0]?.native;
      if (!native || seen.has(native)) continue;
      seen.add(native);
      list.push(native);
    }
    out[cat.id] = list;
  }
  return out;
}

const CATEGORY_EMOJIS = buildCategoryEmojis();

function firstGrapheme(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  try {
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
      const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      const iter = seg.segment(trimmed)[Symbol.iterator]();
      const first = iter.next();
      if (!first.done && first.value?.segment) return first.value.segment;
    }
  } catch {
    /* fall through */
  }
  return Array.from(trimmed)[0] ?? '';
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (emoji: string | undefined) => void;
  /** 피커 앵커(아이콘 버튼) — 바깥 클릭 닫기에서 제외 */
  anchorRef?: React.RefObject<HTMLElement | null>;
};

/** 포인트 세트 아이콘용 이모지 피커 (전체 카탈로그 + 카테고리 + 직접 입력) */
const PointTypeEmojiPicker: React.FC<Props> = ({ open, onClose, onSelect, anchorRef }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const [custom, setCustom] = useState('');
  const [categoryId, setCategoryId] = useState(CATEGORY_META[0]?.id ?? 'people');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const emojis = useMemo(() => CATEGORY_EMOJIS[categoryId] ?? [], [categoryId]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = Math.min(420, window.innerWidth - 16);
      let left = r.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - width);
      let top = r.bottom + 6;
      const approxHeight = 420;
      if (top + approxHeight > window.innerHeight - 8) {
        top = Math.max(8, r.top - 6 - approxHeight);
      }
      setPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) {
      setCustom('');
      setCategoryId(CATEGORY_META[0]?.id ?? 'people');
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef?.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !pos || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      className="cp-pin-emoji-picker"
      role="dialog"
      aria-label="이모지 선택"
      style={{ top: pos.top, left: pos.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="cp-pin-emoji-picker-main">
        <nav className="cp-pin-emoji-picker-cats" aria-label="이모지 카테고리">
          {CATEGORY_META.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`cp-pin-emoji-picker-cat${categoryId === cat.id ? ' is-active' : ''}`}
              title={cat.label}
              aria-label={cat.label}
              aria-pressed={categoryId === cat.id}
              onClick={() => setCategoryId(cat.id)}
            >
              <span aria-hidden>{cat.icon}</span>
              <span className="cp-pin-emoji-picker-cat-label">{cat.label}</span>
            </button>
          ))}
        </nav>
        <div className="cp-pin-emoji-picker-grid" key={categoryId}>
          {emojis.map((em) => (
            <button
              key={em}
              type="button"
              className="cp-pin-emoji-picker-cell"
              onClick={() => {
                onSelect(em);
                onClose();
              }}
              aria-label={`이모지 ${em}`}
            >
              {em}
            </button>
          ))}
        </div>
      </div>
      <div className="cp-pin-emoji-picker-custom">
        <input
          id={inputId}
          type="text"
          className="cp-pin-emoji-picker-input"
          value={custom}
          placeholder="직접 입력"
          aria-label="직접 입력"
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            const g = firstGrapheme(custom);
            if (!g) return;
            onSelect(g);
            onClose();
          }}
        />
        <button
          type="button"
          className="cp-pin-modal-btn cp-pin-modal-btn--secondary cp-pin-emoji-picker-apply"
          disabled={!firstGrapheme(custom)}
          onClick={() => {
            const g = firstGrapheme(custom);
            if (!g) return;
            onSelect(g);
            onClose();
          }}
        >
          적용
        </button>
      </div>
      <button
        type="button"
        className="cp-pin-emoji-picker-reset"
        onClick={() => {
          onSelect(undefined);
          onClose();
        }}
      >
        기본 별 아이콘으로 설정
      </button>
    </div>,
    document.body
  );
};

export default PointTypeEmojiPicker;
